import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { websocketManager } from 'src/common/services';
import { WebSocketAction } from 'src/common/types';
import { formatNotificationMessage } from 'src/common/utils/notificationUtils';
import { getRelativeTime } from 'src/common/utils/dateUtils';
import { CircleAlert } from 'lucide-react';

// Interface for notification data structure
interface NotificationData {
  alarmType: "alert" | "not_applied" | "alert as priority"
  deviceName: string;
  message: string;
  eventCreatedAt: string;
  eventId: string;
}

// Props for configuring banner behavior
interface NotificationBannerProps {
  createNewSession?: boolean;  // If true, navigates to new VQA session; if false, uses custom handler
  onNotificationClick?: (data: NotificationData) => void;  // Custom click handler for existing sessions
}

export function NotificationBanner({ createNewSession = true, onNotificationClick }: NotificationBannerProps) {
  const navigate = useNavigate();
  
  // Banner visibility state
  const [showNotification, setShowNotification] = useState(false);
  
  // Notification content data (with default fallback values)
  const [notificationData, setNotificationData] = useState<NotificationData>({
    alarmType: "alert as priority",
    deviceName: 'Unknown Device',
    message: 'Unknown Messages',
    eventCreatedAt: 'Unknown Timestamp',
    eventId: 'unknown'
  });
  
  // Timer reference for auto-hide functionality
  const bannerTimeoutRef = useRef<number | null>(null);

  // Listen for WebSocket notification messages
  useEffect(() => {
    const handleNotificationMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        
        // Only process notification action messages
        if (message.action === WebSocketAction.NOTIFICATION) {
          // Extract notification data with fallback values
          const data = {
            alarmType: message.alarmType || "",
            deviceName: message.deviceName || 'Unknown Device',
            message: message.message || 'Unknown Messages',
            eventCreatedAt: message.eventCreatedAt || new Date().toLocaleString(),
            eventId: message.eventId || 'unknown'
          };
          
          // Update banner content and show it
          setNotificationData(data);
          setShowNotification(true);
          
          // Clear any existing auto-hide timer
          if (bannerTimeoutRef.current) {
            clearTimeout(bannerTimeoutRef.current);
          }
          
          // Set new auto-hide timer (10 seconds)
          bannerTimeoutRef.current = window.setTimeout(() => {
            setShowNotification(false);
          }, 10000);
        }
      } catch (error) {
        console.error('Error parsing notification message:', error);
      }
    };

    // Add WebSocket listener and cleanup on unmount
    websocketManager.addListener(handleNotificationMessage);
    return () => websocketManager.removeListener(handleNotificationMessage);
  }, []);

  // Handle banner click - either custom handler or navigation
  const handleClick = () => {
    // Hide banner immediately
    setShowNotification(false);
    
    // Clear auto-hide timer
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
      bannerTimeoutRef.current = null;
    }

    // Use custom click handler if provided (for existing chat sessions)
    if (onNotificationClick) {
      onNotificationClick(notificationData);
    } 
    // Otherwise navigate to new VQA session with formatted message
    else if (createNewSession) {
      const chatMessage = formatNotificationMessage(notificationData);
      navigate('/rogers-vqa', { state: { initialMessage: chatMessage, eventId: notificationData.eventId } });
    }
  };

  if (!showNotification) return null;

  return (
    <div className="fixed top-0 right-0 w-full z-50 slide-in">
      <div 
        className="m-4 bg-gray-700/95 dark:bg-gray-600/95 backdrop-blur-xl border border-gray-600/50 dark:border-gray-600/50 rounded-xl shadow-2xl p-4 hover:border-blue-500/50 dark:hover:border-blue-500/50 transition-all cursor-pointer light:bg-white/95 light:border-gray-300"
        onClick={handleClick}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            {
              notificationData.alarmType === "alert as priority" ? <div>❗️</div>
              : <div className={`w-2 h-2 ${"bg-blue-500"} rounded-full animate-pulse shadow-lg shadow-blue-500/50`}></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="text-foreground font-semibold">{notificationData.deviceName}</div>
              <div className="text-xs text-muted-foreground">{getRelativeTime(notificationData.eventCreatedAt)}</div>
            </div>
            <div className="text-gray-200 light:text-gray-800 text-sm leading-relaxed">
              {notificationData.message}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

